import { PACKET_ENUM, SVG_ENUM } from "enums";
import { PACKET_PRICE_ENUM } from "enums/PacketsEnum";
import moment from "moment";
import React from "react";
import { createPortal } from "react-dom";
import { Button, SVG } from "shared";
import { ParentType } from "../../PacketModal";
import { useGetSubscription } from "hooks/subscriptions";
import { useGetMe } from "hooks/auth";
import { isDesktop } from "utils";
import { Tooltip } from "react-tooltip";

interface SinglePacketProps {
  packetName: PACKET_ENUM;
  currentPacket: PACKET_ENUM | null;
  selectedPacket: PACKET_ENUM | null;
  setSelectedPacket: (packet: PACKET_ENUM | null) => void;
  parent: ParentType;
}

const SinglePacket: React.FC<SinglePacketProps> = ({
  packetName,
  currentPacket,
  selectedPacket,
  setSelectedPacket,
  parent,
}) => {
  const { data: authorizedUser } = useGetMe();
  const { data: subscription } = useGetSubscription(!!authorizedUser);
  if (!packetName) return null;
  const packetNameLarge: any = packetName ? packetName.toUpperCase() : "";
  const packetPrice = PACKET_PRICE_ENUM[packetNameLarge];

  const isMoreThanBasic = packetName !== PACKET_ENUM.BASIC;
  const isMoreThanStandard =
    packetName !== PACKET_ENUM.STANDARD && isMoreThanBasic;
  const isMoreThanPremium =
    packetName !== PACKET_ENUM.PREMIUM && isMoreThanStandard;

  const isActive = currentPacket === packetName;
  const isSelected = selectedPacket === packetName;
  const wrapperNode = document.querySelector(".choose-packet-step__wrapper");
  const buttonsNode = document.querySelector(
    ".choose-packet-step__submit-buttons"
  );

  const isFromPayment = parent === "payment";

  const PacketValues = Object.keys(PACKET_ENUM);

  const isUpgrade =
    PacketValues.indexOf(currentPacket ? currentPacket.toUpperCase() : "") <
    PacketValues.indexOf(packetName ? packetName.toUpperCase() : "");
  const endOfPaymentCycle = moment().add(1, "month");

  const handleSetSelectedPacket = (packetName: PACKET_ENUM) => {
    setSelectedPacket(packetName);

    if (isDesktop()) {
      setTimeout(() => {
        const modalContent = document.querySelector(
          ".fullscreen-modal-overlay"
        );
        if (modalContent) {
          const targetElement = modalContent.querySelector(
            ".choose-packet-step__button"
          ) as HTMLElement;

          if (!targetElement) return;

          const elementOffsetTop = targetElement.offsetTop;

          modalContent.scrollTo({
            top: elementOffsetTop + 350,
            behavior: "smooth",
          });
        } else {
          const targetElement = document.querySelector(
            ".choose-packet-step__button"
          );

          if (!targetElement) return;

          targetElement.scrollIntoView({ behavior: "smooth" });
        }
      }, 200);
    }
  };

  return (
    <div className="choose-packet-step__wrapper">
      <div
        className={`choose-packet-step__packet 
        choose-packet-step__packet--${packetName} 
        ${isActive ? "choose-packet-step__packet--active" : ""}
        ${isSelected ? "choose-packet-step__packet--selected" : ""}
        `}
      >
        {isMoreThanPremium && (
          <span className="choose-packet-step__award">
            <SVG type={SVG_ENUM.AWARD} />
          </span>
        )}
        <h2 className="choose-packet-step__packet-name">
          PAKIET {packetName?.toUpperCase()}
        </h2>
        <h4 className="choose-packet-step__price">
          <span>{packetPrice || 0}</span>zł/msc
        </h4>
        {isActive ? (
          <span className="choose-packet-step__actual">AKTUALNY</span>
        ) : (
          <>
            {isSelected ? (
              <span
                className={`choose-packet-step__selected ${
                  isSelected ? "choose-packet-step__selected--active" : ""
                }`}
              >
                Wybrano <SVG type={SVG_ENUM.CIRCLE_CHECK} />
              </span>
            ) : (
              <Button
                className="button--rounded button--stroke choose-packet-step__button"
                label="Wybierz"
                onClick={() => {
                  handleSetSelectedPacket(packetName);
                }}
              />
            )}
          </>
        )}
        <hr />
        <ul className="choose-packet-step__ul">
          <li className="choose-packet-step__li">
            <SVG type={SVG_ENUM.CHECK_VERIFIED} />
            <div>
              Informacje rynkowe o spółkach, w&nbsp;które Klient zainwestował
              przy wsparciu <span>Reliance Nowych Technologii ASI S.A.</span>
            </div>
          </li>
          {isMoreThanBasic && (
            <li className="choose-packet-step__li">
              <SVG type={SVG_ENUM.CHECK_VERIFIED} />
              <div>
                Informacje rynkowe o wszystkich spółkach aktualnie dostępnych
                w&nbsp;ofercie inwestycyjnej{" "}
                <span>Reliance Nowych Technologii ASI S.A.</span>
              </div>
            </li>
          )}
          {isMoreThanStandard && (
            <li className="choose-packet-step__li">
              <SVG type={SVG_ENUM.CHECK_VERIFIED} />
              <div>
                Informacje rynkowe{" "}
                <span>o {isMoreThanPremium ? 10 : 2} wybranych spółkach</span>,
                w&nbsp;które Klient zainwestował samodzielnie, poza ofertą{" "}
                <span>Reliance Nowych Technologii ASI S.A.</span>
              </div>
            </li>
          )}
          {isMoreThanPremium && (
            <li className="choose-packet-step__li choose-packet-step__li--bold">
              <SVG type={SVG_ENUM.CHECK_VERIFIED} />
              <div>
                Jedna analiza rynkowa miesięcznie, obejmująca spółkę wskazaną
                przez Klienta.
              </div>
              <div>
                <div
                  className="tooltip-white"
                  data-tooltip-id="analysis-tooltip"
                >
                  <SVG type={SVG_ENUM.INFO} />
                </div>

                <Tooltip
                  place="top"
                  className="custom-tooltip-light analysis-tooltip"
                  id="analysis-tooltip"
                  render={() => (
                    <ul>
                      <li>Co miesiąc można wybrać inną spółkę do analizy;</li>
                      <li>
                        Analiza trwa zazwyczaj 7 dni roboczych od akceptacji
                        wniosku;
                      </li>
                      <li>
                        Reliance Nowych Technologii ASI S.A. zastrzega sobie
                        prawo do odrzucenia wniosku o analizę – w takim
                        przypadku użytkownik może zlecić do analizy inną spółkę;
                      </li>
                    </ul>
                  )}
                />
              </div>
            </li>
          )}
        </ul>
      </div>
      {isSelected && wrapperNode && isFromPayment && authorizedUser?.package
        ? createPortal(
            isUpgrade ? (
              <p
                className={`choose-packet-step__description choose-packet-step__description--${packetName}`}
              >
                Zmiana pakietu nastąpi od razu, jednak płatność zostanie pobrana
                na początku nowego miesiąca.
              </p>
            ) : (
              <p
                className={`choose-packet-step__description choose-packet-step__description--${packetName}`}
              >
                Zmiana pakietu nastąpi{" "}
                {subscription?.current_period_end ? (
                  <b>
                    {" "}
                    {moment
                      .unix(subscription?.current_period_end)
                      .format("DD/MM/YYYY")}
                    ,{" "}
                  </b>
                ) : (
                  moment(endOfPaymentCycle).format("DD/MM/YYYY")
                )}
                jednak usługi z obecnego pakietu będą aktywne do końca bieżącego
                okresu rozliczeniowego.
              </p>
            ),
            // @ts-ignore
            isDesktop() ? wrapperNode : buttonsNode
          )
        : null}
    </div>
  );
};

export default SinglePacket;
